import { useEffect, useRef, useState } from 'react';
import { Clock, Search, Star } from 'lucide-react';
import { minutesToHHMM } from '@garlic/shared';
import { formatBRL } from '../lib/format';
import { cx } from '../ui/primitives';
import type { MenuCategory, MenuProduct } from './types';

/** Lista do cardápio com categorias fixas no topo (rolagem até a seção) e busca. */
export function MenuBrowser({ categories, onSelect, stickyTop = 0 }: { categories: MenuCategory[]; onSelect: (p: MenuProduct, c: MenuCategory) => void; stickyTop?: number }) {
  const [active, setActive] = useState(categories[0]?.id);
  const [query, setQuery] = useState('');
  const sections = useRef(new Map<string, HTMLElement>());
  const chips = useRef<HTMLDivElement>(null);

  // Destaca a categoria visível enquanto o cliente rola.
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id.replace('cat-', ''));
      },
      { rootMargin: `-${stickyTop + 64}px 0px -60% 0px` },
    );
    sections.current.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [categories, stickyTop, query]);

  useEffect(() => {
    chips.current?.querySelector(`[data-cat="${active}"]`)?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [active]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? categories
        .map((c) => ({ ...c, products: c.products.filter((p) => `${p.name} ${p.description ?? ''}`.toLowerCase().includes(q)) }))
        .filter((c) => c.products.length)
    : categories;

  return (
    <div>
      <div className="sticky z-20 -mx-4 border-b border-line bg-paper/95 px-4 pb-2 pt-3 backdrop-blur" style={{ top: stickyTop }}>
        <div className="relative mb-2">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar no cardápio"
            aria-label="Buscar no cardápio"
            className="h-10 w-full rounded-xl border border-line bg-white pl-9 pr-3 text-sm focus:border-garlic-500 focus:outline-none focus:ring-4 focus:ring-garlic-100"
          />
        </div>
        {!q && (
          <div ref={chips} className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none]">
            {categories.map((c) => (
              <button
                key={c.id}
                data-cat={c.id}
                onClick={() => sections.current.get(c.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className={cx(
                  'shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
                  active === c.id ? 'bg-[var(--brand)] text-[var(--brand-contrast)]' : 'bg-white text-ink-2 ring-1 ring-line hover:bg-paper-2',
                )}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {filtered.length === 0 && <p className="py-12 text-center text-sm text-ink-3">Nada encontrado para “{query}”.</p>}

      {filtered.map((c) => (
        <section
          key={c.id}
          id={`cat-${c.id}`}
          ref={(el) => {
            if (el) sections.current.set(c.id, el);
            else sections.current.delete(c.id);
          }}
          className="pt-6"
          style={{ scrollMarginTop: stickyTop + 110 }}
        >
          <h2 className="font-display text-xl font-semibold">{c.name}</h2>
          {c.description && <p className="mt-0.5 text-sm text-ink-3">{c.description}</p>}
          {!c.availableNow && c.availableFrom != null && (
            <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-warn-600">
              <Clock className="size-3.5" /> Disponível das {minutesToHHMM(c.availableFrom)} às {minutesToHHMM(c.availableTo!)}
            </p>
          )}
          <ul className="mt-3 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-white">
            {c.products.map((p) => {
              const disabled = !p.available || !c.availableNow;
              return (
                <li key={p.id}>
                  <button
                    onClick={() => onSelect(p, c)}
                    className={cx('flex w-full gap-3 p-4 text-left transition-colors hover:bg-paper/70', disabled && 'opacity-60')}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 font-medium">
                        {p.featured && <Star className="size-3.5 shrink-0 fill-amber-400 text-amber-400" aria-label="Destaque" />}
                        {p.name}
                      </p>
                      {p.description && <p className="mt-0.5 line-clamp-2 text-sm text-ink-3">{p.description}</p>}
                      <p className="mt-1.5 text-sm font-semibold">
                        {p.soldOut ? <span className="text-danger-600">Esgotado</span> : !p.available ? <span className="text-ink-3">Indisponível</span> : formatBRL(p.priceCents)}
                        {p.available && p.optionGroups.some((g) => g.options.some((o) => o.priceCents > 0)) && <span className="ml-1 text-xs font-normal text-ink-3">+ adicionais</span>}
                      </p>
                    </div>
                    {p.imagePath && <img src={p.imagePath} alt="" loading="lazy" className="size-20 shrink-0 rounded-xl object-cover" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
